function isNumericDesignationId(value) {
  if (value == null || value === "") return false;
  return !Number.isNaN(Number(value));
}

/** True when value is a human-readable designation label (not a numeric code). */
export function isDesignationLabel(value, designationId) {
  if (value == null || value === "") return false;
  const label = String(value).trim();
  if (!label) return false;
  if (label === String(designationId)) return false;
  return !isNumericDesignationId(label);
}

/**
 * Build map: employeeDesignationId → employeeDesignationName
 * from workForce/getListOfDesignations (or similar) API data.
 */
export function buildDesignationLookupMap(...designationLists) {
  const map = new Map();

  for (const list of designationLists) {
    if (!Array.isArray(list)) continue;

    for (const item of list) {
      const id = item?.employeeDesignationId;
      const name = item?.employeeDesignationName;
      if (id != null && id !== "" && name) {
        map.set(String(id), String(name).trim());
      }
    }
  }

  return map;
}

/** Get employeeDesignationName for a given employeeDesignationId. */
export function lookupDesignationName(designationId, lookupMap) {
  if (designationId == null || designationId === "" || !(lookupMap instanceof Map)) {
    return "";
  }
  return lookupMap.get(String(designationId)) || "";
}

/** Find employeeDesignationId when only the name (e.g. LS-I) is known. */
export function findDesignationIdByName(name, ...designationLists) {
  if (!name) return null;
  const target = String(name).trim().toLowerCase();
  if (!target) return null;

  for (const list of designationLists) {
    if (!Array.isArray(list)) continue;
    const match = list.find(
      (item) =>
        String(item?.employeeDesignationName || "")
          .trim()
          .toLowerCase() === target
    );
    if (match?.employeeDesignationId != null) {
      return match.employeeDesignationId;
    }
  }

  return null;
}

export function getDesignationIdFromItem(item) {
  if (!item || typeof item !== "object") return null;
  if (item.employeeDesignationId != null && item.employeeDesignationId !== "") {
    return item.employeeDesignationId;
  }
  return item.designationId ?? item.designation ?? null;
}

export function getDesignationNameFromItem(item) {
  if (!item || typeof item !== "object") return "";
  return item.employeeDesignationName ?? item.designationName ?? "";
}

/**
 * Designation id on getAllEmployeesList rows is usually in `roleName`
 * (matches employeeDesignationId on getListOfDesignations).
 */
export function getEmployeeDesignationId(item) {
  if (!item || typeof item !== "object") return null;

  if (item.employeeDesignationId != null && item.employeeDesignationId !== "") {
    return item.employeeDesignationId;
  }
  if (item.designation != null && item.designation !== "") {
    return item.designation;
  }
  if (item.designationId != null && item.designationId !== "") {
    return item.designationId;
  }
  if (item.roleName != null && item.roleName !== "" && isNumericDesignationId(item.roleName)) {
    return item.roleName;
  }

  return null;
}

/** Resolve id + name for one employee row from getAllEmployeesList. */
export function resolveEmployeeDesignation(item, lookupMap, ...designationLists) {
  let designationId = getEmployeeDesignationId(item);

  if (
    (designationId == null || designationId === "") &&
    isDesignationLabel(item?.roleName, null)
  ) {
    designationId = findDesignationIdByName(item.roleName, ...designationLists);
  }

  let designationName =
    item?.employeeDesignationName ||
    lookupDesignationName(designationId, lookupMap) ||
    "";

  if (!designationName && isDesignationLabel(item?.roleName, designationId)) {
    designationName = String(item.roleName).trim();
  }

  if (!designationName && designationId != null) {
    designationName = lookupDesignationName(designationId, lookupMap);
  }

  return {
    designationId,
    designationName,
  };
}

/** Name from stored workforce row (not the numeric id). */
export function pickEmployeeDesignationName(row) {
  if (!row || typeof row !== "object") return "";
  const id = row.designation;

  if (isDesignationLabel(row.designationName, id)) {
    return String(row.designationName).trim();
  }
  if (isDesignationLabel(row.employeeDesignationName, id)) {
    return String(row.employeeDesignationName).trim();
  }
  if (isDesignationLabel(row.roleName, id)) {
    return String(row.roleName).trim();
  }

  return "";
}

export function pickEmployeeDesignationId(item) {
  if (!item || typeof item !== "object") return null;
  return getEmployeeDesignationId(item);
}

/** Resolve designation id to employeeDesignationName using API list(s). */
export function resolveDesignationName(designationId, ...designationListsAndMap) {
  if (designationId == null || designationId === "") {
    return "";
  }

  for (const arg of designationListsAndMap) {
    if (arg instanceof Map) {
      const fromMap = arg.get(String(designationId));
      if (fromMap) return fromMap;
    }
  }

  const lists = designationListsAndMap.filter(Array.isArray);
  const lookupMap = buildDesignationLookupMap(...lists);
  return lookupDesignationName(designationId, lookupMap);
}

/**
 * Display label for UI — always employeeDesignationName, never employeeDesignationId.
 */
export function getDesignationDisplayLabel(
  designationId,
  designationName = "",
  lookupMap,
  ...designationLists
) {
  const map =
    lookupMap instanceof Map
      ? lookupMap
      : buildDesignationLookupMap(...[lookupMap, ...designationLists].filter(Array.isArray));

  const fromMap = lookupDesignationName(designationId, map);
  if (fromMap) return fromMap;

  if (isDesignationLabel(designationName, designationId)) {
    return String(designationName).trim();
  }

  const resolved = resolveDesignationName(designationId, map, ...designationLists.filter(Array.isArray));
  if (resolved) return resolved;

  return "—";
}

/** Attach designationName (employeeDesignationName) to each workforce row. */
export function enrichWorkforceRows(rows, lookupMap, ...designationLists) {
  if (!Array.isArray(rows)) return [];

  const map =
    lookupMap instanceof Map
      ? lookupMap
      : buildDesignationLookupMap(lookupMap, ...designationLists.filter(Array.isArray));

  const lists = designationLists.filter(Array.isArray);
  if (!lists.length && lookupMap instanceof Map) {
    // map only — ok
  }

  return rows.map((row) => {
    const resolved = resolveEmployeeDesignation(
      {
        employeeDesignationId: row.designation ?? row.roleName,
        employeeDesignationName: row.designationName,
        designation: row.designation,
        roleName: row.roleName,
      },
      map,
      ...lists
    );

    const designationId = resolved.designationId ?? row.designation;
    const label = getDesignationDisplayLabel(
      designationId,
      resolved.designationName || pickEmployeeDesignationName(row),
      map,
      ...lists
    );

    return {
      ...row,
      designation: designationId,
      designationName: label === "—" ? "" : label,
    };
  });
}

/** Field-staff options for dropdowns (isFieldStaff === 1). */
export function filterFieldStaffDesignations(designationList) {
  if (!Array.isArray(designationList)) return [];
  return designationList.filter((item) => item?.isFieldStaff === 1);
}
